import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Countdown, LiveViewers, discountInfo, saleEndDate, getProductBrand, getProductCategoryName } from './components/productExtras';
import { useParams, Link } from 'react-router-dom';
import {
  ShoppingCart, Heart, User, Minus, Plus, ArrowLeft,
  MagnifyingGlass, House, Phone, Envelope, Truck, ShieldCheck,
  FacebookLogo, InstagramLogo, TiktokLogo, Star, List, X, Tag, CreditCard
} from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import PaypalPayLater from '../../components/PaypalPayLater';
import SeoHead from '../../components/SeoHead';
import { useAuth } from '../../context/AuthContext';
import { sanitizeHTML } from '../../utils/sanitize';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { MrgHeader as MagereHeader } from './MrgHeader';
import { MrgFooter as MagereFooter } from './MrgFooter';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "mrg";

const ProductPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug, productSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [store, setStore] = useState(null);
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedAttrs, setSelectedAttrs] = useState({});
  const { addItem, itemCount, setIsOpen, setStoreSlug } = useCart();
  const { user } = useAuth();
  const userType = user?.client_type || 'particulier';
  const { recentlyViewed, trackProduct } = useRecentlyViewed(storeSlug);

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  const isMagere = store?.identity?.theme_template === 'magere' || storeSlug === 'mrg' || storeSlug === 'magere-viandes' || standalone;
  const colors = useMemo(() => {
    const si = store?.identity || {};
    return {
      primary: si.primary_color || '#c2185b',
      secondary: si.secondary_color || '#0cb5c6',
      dark: '#2b2b2b',
      darkFooter: '#1a1a1a',
    };
  }, [store]);
  const logoUrl = store?.identity?.logo_light || '';

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/s/${storeSlug}`),
      axios.get(`${API}/api/s/${storeSlug}/products/${productSlug}`),
      axios.get(`${API}/api/s/${storeSlug}/categories`),
    ]).then(([s, p, c]) => {
      setStore(s.data);
      setProduct(p.data);
      setCategories(c.data || []);
      setSelectedImage(0);
      setQuantity(1);
    }).catch(console.error).finally(() => setLoading(false));
  }, [storeSlug, productSlug]);

  /* Track product view */
  useEffect(() => {
    if (product) {
      trackProduct(product);
      // Increment server-side view counter (fire-and-forget)
      if (store?.id && product.id) {
        axios.post(`${API}/api/stores/${store.id}/products/${product.id}/track-view`).catch(() => {});
      }
    }
  }, [product, trackProduct, store?.id]);

  /* ── Variation logic ── */
  const isVariable = product?.product_type === 'variable' && product?.variations?.length > 0;
  const variationAttrs = isVariable
    ? product.attributes?.filter(a => a.used_for_variations && a.values?.length) || []
    : [];

  // Init selectedAttrs with first values when product loads
  useEffect(() => {
    if (isVariable && variationAttrs.length > 0) {
      const init = {};
      variationAttrs.forEach(a => { init[a.name] = a.values[0]; });
      setSelectedAttrs(init);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const activeVariation = isVariable
    ? product.variations.find(v =>
        v.enabled !== false &&
        v.attributes &&
        variationAttrs.every(a => v.attributes[a.name] === selectedAttrs[a.name])
      )
    : null;

  // When a variation is selected and has an image, switch to it
  useEffect(() => {
    if (activeVariation?.image && product?.images) {
      const idx = product.images.indexOf(activeVariation.image);
      if (idx >= 0) setSelectedImage(idx);
      else setSelectedImage(0); // variation image might not be in gallery
    }
  }, [activeVariation, product?.images]);

  // Auto-rotate the product image gallery (pauses on hover)
  useEffect(() => {
    const imgs = product?.images || [];
    if (galleryPaused || imgs.length < 2) return undefined;
    const t = setInterval(() => {
      setSelectedImage(prev => (prev + 1) % imgs.length);
    }, 3500);
    return () => clearInterval(t);
  }, [product?.images, galleryPaused]);

  const isPro = userType === 'pro';
  const hasProPrice = isPro && product?.price_pro && parseFloat(product.price_pro) < parseFloat(product.price);
  const displayPrice = hasProPrice ? product.price_pro : (activeVariation ? (activeVariation.sale_price || activeVariation.price) : (product?.sale_price || product?.price));
  const originalPrice = hasProPrice ? product.price : (activeVariation ? (activeVariation.sale_price ? activeVariation.price : null) : (product?.sale_price ? product?.price : null));
  const isPromoDisplay = !!originalPrice;

  // Volume discounts info
  const volumeDiscounts = product?.volume_discounts || [];
  const activeVolumeDiscount = volumeDiscounts.length > 0
    ? [...volumeDiscounts].sort((a, b) => (b.min_qty || 0) - (a.min_qty || 0)).find(vd => quantity >= (vd.min_qty || 0))
    : null;
  const finalDisplayPrice = activeVolumeDiscount ? activeVolumeDiscount.price : displayPrice;

  const handleAdd = () => {
    if (!product) return;
    const variationData = activeVariation ? { attributes: selectedAttrs, price: activeVariation.sale_price || activeVariation.price } : null;
    addItem(product, quantity, variationData, storeSlug, userType);
    setIsOpen(true);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600" /></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center bg-white"><h1 className="text-2xl font-bold text-slate-600">Produit introuvable</h1></div>;

  const images = product.images || [];
  const suggested = product.suggested || [];
  const isPromo = isPromoDisplay;
  const fontFamily = isMagere ? "'Poppins', sans-serif" : "'Inter', sans-serif";
  const variationImage = activeVariation?.image;

  /* ═══════════ TPS HEADER ═══════════ */
  const TpsHeader = () => (
    <header className="sticky top-0 z-50">
      <div className="bg-white">
        <div className="max-w-[1300px] mx-auto px-6 flex items-center justify-between" style={{ height: 90 }}>
          <Link to={getLink('/')}>{logoUrl ? <img src={logoUrl} alt={store?.name} className="h-[65px] max-w-[260px] object-contain" />
            : <span className="text-2xl font-extrabold" style={{ color: colors.primary }}>{store?.name}</span>}</Link>
          <div className="flex items-center gap-5">
            <Link to={getLink('/login')} className="hidden md:flex flex-col items-center gap-0.5 text-slate-600"><User size={26} weight="thin" /><span className="text-[10px]">Compte</span></Link>
            <button className="relative flex flex-col items-center gap-0.5 text-slate-600" onClick={() => setIsOpen(true)}>
              <ShoppingCart size={26} weight="thin" /><span className="text-[10px]">Panier</span>
              <span className="absolute -top-1.5 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: colors.secondary }}>{itemCount}</span>
            </button>
          </div>
        </div>
      </div>
      <nav style={{ backgroundColor: colors.primary }}>
        <div className="max-w-[1300px] mx-auto px-6 flex items-center h-[44px]">
          <Link to={getLink('/')} className="px-4 h-[44px] flex items-center text-[13px] font-medium text-white hover:bg-white/10">Accueil</Link>
        </div>
      </nav>
    </header>
  );

  const HeaderComp = isMagere ? () => <MagereHeader store={store} standalone={standalone} /> : TpsHeader;

  const imgSrc = (url) => url && url.startsWith('/') ? `${API}${url}` : url;

  /* ═══════════ SUGGESTED PRODUCT CARD ═══════════ */
  const SuggestedCard = ({ p }) => (
    <Link to={getLink(`/product/${p.slug}`)}
      className="group block bg-white border border-slate-100 hover:shadow-xl transition-all rounded-lg overflow-hidden">
      <div className="aspect-square overflow-hidden bg-slate-50 relative">
        {(p.featured_image || p.images?.[0]) && <img src={imgSrc(p.featured_image || p.images[0])} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500" loading="lazy" />}
      </div>
      <div className="p-3 text-center">
        <h3 className="text-[12px] font-semibold text-slate-800 line-clamp-2 mb-1 min-h-[32px]">{p.name}</h3>
        <span className="text-[15px] font-bold" style={{ color: colors.primary }}>{parseFloat(p.is_promo && p.sale_price ? p.sale_price : p.price).toFixed(2)} €</span>
        <span className="text-[11px] text-slate-400 block">{(parseFloat(p.is_promo && p.sale_price ? p.sale_price : p.price) / (1 + (typeof p.tax_rate === 'number' ? p.tax_rate : 5.5) / 100)).toFixed(2)} € HT</span>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" style={{ fontFamily }}>
      <SeoHead
        title={`${product.name} — ${store?.name || 'MRG Optique & Audition'}`}
        description={(product.short_description || product.description || `Découvrez ${product.name} chez MRG Optique & Audition — Opticien & Audioprothésiste à Istres (13800). Tiers payant mutuelles, conseils d'experts.`).replace(/<[^>]+>/g, '').slice(0, 280)}
        image={product.featured_image && (product.featured_image.startsWith('http') ? product.featured_image : `${process.env.REACT_APP_BACKEND_URL}${product.featured_image}`)}
        jsonLd={(() => {
          const price = Number(product.sale_price || product.price || 0);
          const inStock = product.stock_status !== 'outofstock';
          const imgFull = product.featured_image && (product.featured_image.startsWith('http') ? product.featured_image : `${process.env.REACT_APP_BACKEND_URL}${product.featured_image}`);
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          const href = typeof window !== 'undefined' ? window.location.href : undefined;
          const breadcrumbItems = [
            { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${origin}/` },
          ];
          if (product.category) {
            breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": product.category, "item": `${origin}/?category=${encodeURIComponent(product.category)}` });
          }
          breadcrumbItems.push({ "@type": "ListItem", "position": breadcrumbItems.length + 1, "name": product.name, "item": href });
          return [
            {
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": product.name,
              "description": (product.description || product.short_description || product.name).replace(/<[^>]+>/g, '').slice(0, 5000),
              "image": imgFull ? [imgFull] : undefined,
              "sku": product.sku || undefined,
              "brand": { "@type": "Brand", "name": store?.name || "MRG Optique & Audition" },
              "offers": {
                "@type": "Offer",
                "url": href,
                "priceCurrency": "EUR",
                "price": price.toFixed(2),
                "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "itemCondition": "https://schema.org/NewCondition",
                "priceValidUntil": new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
                "seller": { "@type": "Organization", "name": store?.name || "MRG Optique & Audition" },
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": breadcrumbItems,
            },
          ];
        })()}
      />
      {isMagere && <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />}
      <HeaderComp />
      <div className="h-[56px] md:h-[56px]" /> {/* Fixed header spacer */}

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="border-b border-slate-100" style={{ backgroundColor: '#f8f9fb' }}>
          <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center gap-2 text-[12px] text-slate-500">
            <Link to={getLink('/')} className="hover:underline" style={{ color: colors.secondary || colors.primary }}>Accueil</Link>
            <span>/</span>
            {product.category && <><span style={{ color: colors.primary }}>{product.category}</span><span>/</span></>}
            <span className="text-slate-700 font-medium truncate">{product.name}</span>
          </div>
        </div>

        {/* Product detail */}
        <div className="max-w-[1200px] mx-auto px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Images */}
            <div>
              <div className="aspect-square bg-white rounded-2xl overflow-hidden border border-slate-100 mb-4 relative group"
                onMouseEnter={() => setGalleryPaused(true)} onMouseLeave={() => setGalleryPaused(false)}>
                {variationImage && !images.includes(variationImage) ? (
                  <img src={variationImage.startsWith('/') ? `${API}${variationImage}` : variationImage} alt={product.name} className="w-full h-full object-contain p-6" />
                ) : images[selectedImage] ? (
                  <img key={selectedImage} src={images[selectedImage].startsWith('/') ? `${API}${images[selectedImage]}` : images[selectedImage]} alt={product.name} className="w-full h-full object-contain p-6" style={{ animation: 'fadeImg 0.5s ease' }} />
                ) : null}
                {images.length > 1 && !(variationImage && !images.includes(variationImage)) && (
                  <>
                    <button onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md border border-slate-100 flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                      aria-label="Image précédente">‹</button>
                    <button onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md border border-slate-100 flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                      aria-label="Image suivante">›</button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      {images.map((_, i) => (
                        <button key={i} onClick={() => setSelectedImage(i)} aria-label={`Image ${i + 1}`}
                          className="rounded-full transition-all"
                          style={{ width: i === selectedImage ? 18 : 7, height: 7, backgroundColor: i === selectedImage ? colors.primary : '#cbd5e1' }} />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setSelectedImage(i)}
                      className="w-[70px] h-[70px] flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all"
                      style={{ borderColor: i === selectedImage ? colors.primary : '#e5e7eb' }}>
                      <img src={img.startsWith('/') ? `${API}${img}` : img} alt="" className="w-full h-full object-contain p-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {(() => {
                const brand = getProductBrand(product, categories);
                const catName = getProductCategoryName(product, categories);
                return (brand || catName) ? (
                  <div className="flex items-center gap-3 mb-3">
                    {brand && (
                      <span className="inline-flex items-center gap-1.5">
                        <img src={brand.logo} alt={brand.name} className="h-5 max-w-[60px] object-contain" onError={e => { e.target.style.display = 'none'; }} />
                        <span className="text-[12px] font-semibold text-slate-700">{brand.name}</span>
                      </span>
                    )}
                    {catName && <span className="text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ backgroundColor: colors.primary + '12', color: colors.primary }}>{catName}</span>}
                  </div>
                ) : null;
              })()}
              <h1 className="text-[26px] md:text-[32px] font-semibold mb-4" style={{ color: colors.dark || '#1a1a1a' }}>{product.name}</h1>
              <div className="mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[28px] font-semibold" style={{ color: colors.primary }}>
                    {parseFloat(displayPrice).toFixed(2)} €
                  </span>
                  {isPromo && <span className="text-[16px] text-slate-400 line-through">{parseFloat(originalPrice).toFixed(2)} €</span>}
                  {isPromo && (() => {
                    const d = discountInfo({ price: originalPrice, sale_price: displayPrice });
                    return d ? <span className="text-[13px] font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: colors.accent || '#FF004D' }}>-{d.percent}%</span> : null;
                  })()}
                  <small className="text-[12px] text-slate-400">TTC</small>
                </div>
                {isPromo && (() => {
                  const d = discountInfo({ price: originalPrice, sale_price: displayPrice });
                  return d ? <div className="text-[13.5px] font-semibold mt-1" style={{ color: colors.accent || '#FF004D' }}>Économisez {d.amount} €</div> : null;
                })()}
                {(() => { const end = saleEndDate(product); return isPromo && end ? <div className="mt-2.5"><Countdown end={end} accent={colors.accent} /></div> : null; })()}
                <div className="mt-3"><LiveViewers id={product.id} /></div>
                {/* Weight + price per kg (food products only) */}
                {(() => {
                  if (!product.is_food_product) return null;
                  // Use DB weight first; fallback to weight in product name (legacy)
                  let weightKg = typeof product.weight === 'number' && product.weight > 0 ? product.weight : null;
                  if (!weightKg) {
                    const kgMatch = (product.name || '').toUpperCase().match(/(\d+(?:[.,]\d+)?)\s*KG/);
                    if (kgMatch) weightKg = parseFloat(kgMatch[1].replace(',', '.'));
                  }
                  if (!weightKg || weightKg <= 0) return null;
                  const pricePerKg = parseFloat(displayPrice) / weightKg;
                  // Format weight: <1kg → grams, else kg
                  const weightLabel = weightKg < 1 ? `${Math.round(weightKg * 1000)} g` : `${String(weightKg).replace('.', ',')} kg`;
                  return (
                    <div className="flex items-center gap-2 mt-1.5 text-[13px]" data-testid="product-weight-price">
                      <span className="text-slate-500">Poids approx. <strong className="text-slate-700">{weightLabel}</strong></span>
                      <span className="text-slate-300">·</span>
                      <span className="font-semibold" style={{ color: colors.primary }}>{pricePerKg.toFixed(2)} €/kg</span>
                    </div>
                  );
                })()}
                {/* Price last updated badge */}
                {product.price_updated_at && (
                  <p className="text-[11px] text-slate-400 mt-1.5 italic" data-testid="price-updated-at">
                    Prix mis à jour le {new Date(product.price_updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
              {/* Payment in 4x badge */}
              {store?.settings?.enable_payment_4x && (() => {
                const priceFull = parseFloat(displayPrice);
                return priceFull >= 50 ? (
                  <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100" data-testid="payment-4x-badge">
                    <CreditCard size={18} className="text-indigo-600" />
                    <span className="text-[12px] text-indigo-700 font-medium">
                      Payez en 4x sans frais : <strong>4 x {(priceFull / 4).toFixed(2)} EUR</strong>
                    </span>
                  </div>
                ) : null;
              })()}
              {/* PayPal 4x sans frais simulator (auto-shows for eligible amounts) */}
              <div className="mb-4">
                <PaypalPayLater amount={parseFloat(displayPrice)} store={store} variant="inline" />
              </div>
              {isPromo && product.promo_label && (
                <span className="inline-block px-3 py-1.5 rounded-full text-white text-[12px] font-bold mb-4" style={{ backgroundColor: colors.primary }}>{product.promo_label}</span>
              )}
              {product.short_description && (
                <div className="text-[14px] text-slate-600 leading-relaxed mb-6 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.short_description) }} />
              )}

              {/* Variation selectors */}
              {isVariable && variationAttrs.length > 0 && (
                <div className="mb-6 space-y-3" data-testid="variation-selectors">
                  {variationAttrs.map(attr => (
                    <div key={attr.name}>
                      <label className="text-[13px] font-semibold text-slate-700 block mb-1.5">{attr.name}</label>
                      <div className="flex flex-wrap gap-2">
                        {attr.values.map(val => {
                          const isSelected = selectedAttrs[attr.name] === val;
                          return (
                            <button key={val} onClick={() => setSelectedAttrs(prev => ({ ...prev, [attr.name]: val }))}
                              className="px-4 py-2 rounded-lg border-2 text-[13px] font-medium transition-all"
                              style={{
                                borderColor: isSelected ? colors.primary : '#e5e7eb',
                                backgroundColor: isSelected ? colors.primary + '10' : 'white',
                                color: isSelected ? colors.primary : '#475569',
                              }}
                              data-testid={`variation-option-${attr.name}-${val}`}>
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {isVariable && !activeVariation && (
                    <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">Cette combinaison n'est pas disponible.</p>
                  )}
                </div>
              )}

              {/* Quantity + Add to cart */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center border-2 rounded-lg overflow-hidden" style={{ borderColor: colors.primary + '30' }}>
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-slate-50"><Minus size={16} /></button>
                  <span className="px-5 text-[15px] font-bold">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-slate-50"><Plus size={16} /></button>
                </div>
                <button onClick={handleAdd}
                  className="flex-1 py-3.5 rounded-lg text-white text-[15px] font-bold transition-all hover:brightness-110 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: colors.primary }}
                  disabled={isVariable && !activeVariation}
                  data-testid="add-to-cart-btn">
                  <ShoppingCart size={20} weight="bold" /> Ajouter au panier
                </button>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                {[
                  { icon: <Truck size={18} />, label: 'Livraison rapide' },
                  { icon: <ShieldCheck size={18} />, label: 'Paiement sécurisé' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                    <span style={{ color: colors.primary }}>{b.icon}</span>
                    <span className="text-[12px] font-medium text-slate-600">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <div className="mt-12 border-t pt-8">
              <h2 className="text-[20px] font-bold mb-4" style={{ color: colors.primary }}>Description</h2>
              <div className="prose prose-sm max-w-none text-[14px] text-slate-700 leading-relaxed [&_p]:mb-3 [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description) }} />
            </div>
          )}

          {/* Suggested products */}
          {suggested.length > 0 && (
            <div className="mt-14">
              <h2 className="text-[22px] font-bold mb-6" style={{ color: colors.dark || '#1a1a1a' }}>
                Vous aimerez aussi
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {suggested.map(s => <SuggestedCard key={s.id} p={s} />)}
              </div>
            </div>
          )}

          {/* Recently viewed products */}
          {recentlyViewed.filter(p => p.id !== product.id).length > 0 && (
            <div className="mt-14 border-t pt-10" data-testid="recently-viewed-section">
              <h2 className="text-[22px] font-bold mb-6" style={{ color: colors.dark || '#1a1a1a' }}>
                Récemment consultés
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {recentlyViewed.filter(p => p.id !== product.id).map(p => (
                  <Link key={p.id} to={getLink(`/product/${p.slug}`)}
                    className="flex-shrink-0 w-[180px] group bg-white border border-slate-100 hover:shadow-lg transition-all rounded-xl overflow-hidden"
                    data-testid={`recent-product-${p.id}`}>
                    <div className="aspect-square overflow-hidden bg-white">
                      {(p.featured_image || p.images?.[0]) && (
                        <img src={imgSrc(p.featured_image || p.images[0])} alt={p.name}
                          className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                      )}
                    </div>
                    <div className="p-3 text-center">
                      <h3 className="text-[12px] font-semibold text-slate-800 line-clamp-2 mb-1 min-h-[32px]">{p.name}</h3>
                      <span className="text-[15px] font-bold" style={{ color: colors.primary }}>
                        {parseFloat(p.is_promo && p.sale_price ? p.sale_price : p.price).toFixed(2)} €
                      </span>
                      <span className="text-[11px] text-slate-400 block">{(parseFloat(p.is_promo && p.sale_price ? p.sale_price : p.price) / 1.2).toFixed(2)} € HT</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {isMagere ? (
        <MagereFooter store={store} categories={categories} standalone={standalone} />
      ) : (
        <footer style={{ backgroundColor: colors.primary }}>
          <div className="max-w-[1200px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {logoUrl && <img src={logoUrl} alt="" className="h-[50px] object-contain brightness-0 invert" />}
                <span className="text-[12px] text-white/50">&copy; {new Date().getFullYear()} {store?.name}</span>
              </div>
              <div className="flex items-center gap-4">
                <Link to={getLink('/')} className="text-[13px] text-white/70 hover:text-white">Accueil</Link>
                <Link to={getLink('/page/cgv')} className="text-[13px] text-white/70 hover:text-white">CGV</Link>
                <Link to={getLink('/page/contact')} className="text-[13px] text-white/70 hover:text-white">Contact</Link>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default ProductPage;
